import { memo, useState, useEffect, useRef } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { useOverviewData } from './useOverviewData';
import { Globe, Users, FileText, Lightbulb, BarChart3, Building2, Package } from 'lucide-react';

const lastKnownValues = {};

function useAnimatedValue(target, duration = 800, initialValue = 0) {
  const [display, setDisplay] = useState(initialValue);
  const currentRef = useRef(initialValue);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = currentRef.current;
    if (from === target) { setDisplay(target); return; }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const start = performance.now();
    function step(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const val = Math.round(from + (target - from) * eased);
      currentRef.current = val;
      setDisplay(val);
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return display;
}

const KpiCard = memo(function KpiCard({ icon: Icon, label, value, color }) {
  const initialValue = lastKnownValues[label] ?? 0;
  const displayValue = useAnimatedValue(value, 800, initialValue);

  useEffect(() => { lastKnownValues[label] = value; }, [label, value]);

  return (
    <div className="flex-1 min-w-[120px] p-4 rounded-xl bg-[var(--surface-secondary)] border border-[var(--border-primary)] text-center">
      <div className="flex justify-center mb-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
      <div className="text-2xl font-bold text-[var(--text-primary)] tabular-nums">
        {displayValue.toLocaleString()}
      </div>
      <div className="text-xs text-[var(--text-tertiary)] mt-1 font-medium">{label}</div>
    </div>
  );
});

export const KpiStrip = memo(function KpiStrip() {
  const { stats, filteredResults } = useOverviewData();

  if (!stats) return null;

  const policyChanges = stats.byType?.['Policy change'] || 0;
  const innovationDevUse = (stats.byType?.['Innovation development'] || 0) + (stats.byType?.['Innovation use'] || 0);
  const innovationPackages = stats.byType?.['Innovation Package'] || 0;
  const countryCount = stats.countries?.length || 0;

  const metrics = [
    { icon: BarChart3, label: 'Total Results', value: stats.total, color: '#006837' },
    { icon: Globe, label: 'Unique Countries Reported by Results', value: countryCount, color: '#007DBC' },
    { icon: Users, label: 'External Partners', value: stats.externalPartners?.length || 0, color: '#8B5CF6' },
    { icon: Building2, label: 'CGIAR Centers', value: stats.contributingCenters?.length || 0, color: '#2563eb' },
    { icon: FileText, label: 'Policy Changes', value: policyChanges, color: '#F37021' },
    { icon: Lightbulb, label: 'Innovations', value: innovationDevUse, color: '#14B8A6' },
    { icon: Package, label: 'Innovation Packages', value: innovationPackages, color: '#ec4899' },
  ];

  const chartData = metrics.map(m => ({ Metric: m.label, Value: m.value }));

  return (
    <ChartWrapper
      title="Key Performance Indicators From Reported Results"
      info="Summary metrics across all filtered results. Counts update based on your active filters."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="kpi-summary"
    >
      <div className="flex flex-wrap gap-3 justify-center">
        {metrics.map((m) => (
          <KpiCard key={m.label} {...m} />
        ))}
      </div>
    </ChartWrapper>
  );
});
